/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import React, {useEffect, useMemo, useRef, useState} from 'react'
import {useSearchParams} from "react-router-dom";
import {HorizontalTabs} from "../../../../components/Tabs/HorizontalTabs";
import CameraPerformance from "./DetectionCapability/CameraPerformance";
import '../../CameraPerformance.css'
import {LensContent} from "./Lens/LensContent";
import {useMsal} from "@azure/msal-react";

import WithWizardProvider from "../../../../components/Wizard/WithWizardProvider";
import {cameraSlice} from "../../../../../redux/ducks/Wizard/slices";
import {useAppSelector} from "../../../../../redux/hooks";
import {selectGlobalDialogState} from "../../../../../redux/ducks/GlobalDialog/slice";
import CloseGlobalDialogButton from "../../../../components/Global/Dialog/CloseGlobalDialogButton";
import Loader from "../../../../components/Loader/Loader";
import {useHasWriteAccess} from "../../../../../redux/ducks/AccessControl/accessControlHelpers";
import queryString from "query-string";
import {INNERVTAB_URL_PARAM} from "./constants";


import {CircularProgress, IconButton, Toolbar, Tooltip, Typography} from '@mui/material';
import {usePostRequest} from "../../../../../api/helper";
import {selectCurrentUser} from "../../../../../redux/ducks/AccessControl/slice";
import toast from "react-hot-toast";
import {addCameraConfigEndpoint, deleteCameraConfigEndpoint, deleteCameraTempFileEndpoint, getCameraDetailsEndpoint, sensorCameraHeaderTitle, uploadFileCameraEndpoint} from '../../constants';
import ArgosFlexbox from '../../../../components/Containers/ArgosFlexbox';
import NoteAddOutlined from "@mui/icons-material/AutoFixHighOutlined";
import styled from "styled-components"
import {useDispatch} from 'react-redux';
import {config} from '../../../../../AppConfig/config';
import {useSelectWizardOpenc, useSliceActions} from '../../../../../redux/ducks/Wizard/SliceProvider';

import {InstructionsStep} from '../../../../components/Wizard/Steps/InstructionsStep';
import StepUploadFile from '../../../../components/Wizard/Steps/StepUploadFile';
import {WizardDialog} from '../../../../components/Wizard/WizardDialog';
import {errorMessagesTitle, fileProcessingMessagesTitle} from '../../../radar/SensorRadar/constants';
import ImagerStep from '../Wizard/Steps/ImagerStep';
import LensStep from '../Wizard/Steps/LensStep';
import {loginRequest, requestIdToken} from '../../../../../auth/authConfig';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import {ETrackedItemTypes} from "../../../../../constants/trackedItems";
import {ISetRecentlyViewedParams, useSetRecentlyViewedWithDeps} from "../../../../../hooks/useSetRecentlyViewedWithDeps";
import AdasUsage from "../../../../components/AdasUsageTab/AdasUsage";
import {MAP_OBJECT_PROFILE_NAME_TO_KEY} from "../../../radar/SensorRadar/ItemDetails/constants";
import {groupObjectsByTypeNameVersionProfile} from "../../../radar/SensorRadar/ItemDetails/RadarPerformance/CollapsibleTabs/ObjectProperties";
import SensorsEmptyText from '../../../../components/Text/SensorsEmptyText';
import SensorDescription from '../../../radar/SensorRadar/ItemDetails/RadarDescription/SensorDescription';


/**
 * Handles error responses from the API.
 * Displays the error message using toast.
 * 
 * @param res - The response object from the fetch request.
 * @returns {Promise<void>}
 */
export const handleError = async (res: Response, name: string): Promise<void> => {        
  const errorText = await res.text();
  toast.error(errorText + " for " + name);
  return;
}

const Container = styled.div({
  display: 'flex',
  flexFlow: 'column',
  overflow: 'auto',
  height: '100%'
})
/**
 * Runs when clicking on an action in 'Camera Database' table. Renders a header and horizontal tabs.
 */
const ItemDetails = () => {

  const hasWriteAccess = useHasWriteAccess()
  const {instance, accounts} = useMsal();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlParams = Object.fromEntries(searchParams)
  const params: any = useAppSelector(state => {
    const dialogState = selectGlobalDialogState(state)
    if (dialogState.isOpen) {
      return dialogState.component?.params ?? urlParams
    } else return urlParams
  })
  const id = params?.id

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [tabs, setTabs] = useState<any>([])
  const [pageTitle, setPageTitle] = useState("");
  const refTitle = useRef<any>("")
  const currentUserGmid = useAppSelector(state => selectCurrentUser(state).id)

  const [pageDetailsData, setPageDetailsData] = useState<any>({})
  const groupedObjects = useMemo(() => groupObjectsByTypeNameVersionProfile(pageDetailsData.objects), [pageDetailsData.objects])
  const refGroupedObjects = useRef<any>(groupedObjects)
  useEffect(() => {
    refGroupedObjects.current = groupedObjects
  }, [groupedObjects])

  const baselineDataHandle = usePostRequest(getCameraDetailsEndpoint)
  const fetchBaselineData = async () => {
    setIsLoading(true)
    baselineDataHandle({
      "id": id,
      "owner": currentUserGmid,
    }).then((jsonData) => {
      setPageDetailsData(jsonData)
      processData(jsonData)
      setIsLoading(false)
    }).catch(err => {
      setIsLoading(true)
    })
  }

  // Parsing and File download
  const [sensorData, setSensorData] = useState<any>({})
  const [isNewSensor, setIsNewSensor] = useState<any>(true);
  const {setWizardOpen, setWizardMetaData} = useSliceActions()
  const isWizardOpen = useSelectWizardOpenc()
  const dispatch = useDispatch()
  const [isDownloadingFile, setIsDownloadingFile] = useState<boolean>(false)
  const handleClickOpen = () => {
    dispatch(setWizardOpen(true))
  };

  useEffect(() => {
    if (isWizardOpen) return
    fetchBaselineData()
    // Leaving until we can verify TTL rule works
    // checkAndDeleteOrphanFiles()
  }, [isWizardOpen])

  const deleteTempFileHandle = usePostRequest(deleteCameraTempFileEndpoint)
  const checkAndDeleteOrphanFiles = () => {

    // Leaving until we can verify TTL rule works 
    // const _id = sensorData.id
    // const supplier = sensorData.supplier?.replace("/", "_")
    // const component = sensorData.component?.replace("/", "_")
    // const version = sensorData.version
    // const baseFileName = `${supplier}_${component}_${version}_${_id}`
    // const sourceFileName = `${baseFileName}.xlsx`
    // let reqData: any = {
    //   "sourceFileInfo": {
    //     "fileName" : sourceFileName, 
    //     "reqType": "camera"
    //   }
    // }

    // deleteTempFileHandle(reqData)
    //   .then(_response => {
    //   })
    //   .catch(error => {
    //     toast.error("Error deleting file")
    //   })
  }

  const handleDownloadSourceFile = async () => {
    let url = `${config.url.API_URL}/HttpTriggerDownloadFile?request=getSensorCameraSource`
    const idTok = await requestIdToken(instance, accounts)
    const supplier = sensorData.supplier.replace("/", "_")
    const component = sensorData.component.replace("/", "_")
    const version = sensorData.version
    const id = sensorData.id
    //const fileName = `${supplier}_${component}_${version}_${id}.xlsx`
    const sensorName= supplier + " " + component + " V" + version
    const fileName = `${id}.xlsx`
    setIsDownloadingFile(true)
    const req_body = {
      "id": id,
      "fileName": fileName,
      //"supplier": supplier,
      //"component": component,
      //"version": version
    }
    fetch(url, {
      method: "POST",
      headers: {
        'Content-Type': "application/json",
        'Authorization': `Bearer ${idTok}`
      },
      body: JSON.stringify(req_body)
    }).then((res) => {
      if (res.status === 401)
        instance.loginRedirect(loginRequest).catch(e => {
            console.log(e)
          }
        )
      if (res.status === 500){
        handleError(res, sensorName)
      }
      else {
        res.blob().then((blob) => {
          const fileURL = window.URL.createObjectURL(blob)
          let alink = document.createElement('a')
          alink.href = fileURL;
          alink.download = `${supplier}_${component}_${version}_` + fileName
          alink.click();
        })
      }
      setIsDownloadingFile(false)
    }).catch((e) => {
      console.log(e)
    })
  }

  const processData = (jsonData: any) => {
    const numberOfConfigurations = jsonData?.sensor?.configurations?.length
    const tabs: any[] = []
    const id = jsonData.sensor.id
    const supplier = jsonData.sensor.cameraSupplier.replace("/", "_")
    const component = jsonData.sensor.cameraModule.replace("/", "_")
    const version = jsonData.sensor.version
    const sensorData = {supplier, component, version, id}

    setSensorData({supplier, component, version, id})
    let wizardMetaData = {...sensorData, "wizardType": "Camera"}
    dispatch(setWizardMetaData(wizardMetaData))

    let lensContent;
    let detectionCapabilityContent;
    let sensorDescriptionContent;

    if (Object.keys(jsonData?.sensor?.plotData)?.length != 0) {
      setIsNewSensor(false)
      detectionCapabilityContent = <CameraPerformance sensor={jsonData.sensor} objects={jsonData.objects} configNumber={numberOfConfigurations}
                                                      onSaveConfiguration={onSaveConfiguration}
                                                      onDeleteConfiguration={onDeleteConfiguration} gmid={currentUserGmid}/>
      const lensTable = jsonData.sensor.plotData?.lensTable
      const lensColumns = lensTable.headers.map((header: string, index: number) =>
        ({
          "name": header,
          "selector": (row: any[]) => row[index],
          sortable: false,
          center: true,
        }))
      lensContent = <LensContent data={lensTable.rows} columns={lensColumns} jsonData={jsonData}/>
      sensorDescriptionContent= <SensorDescription sensor={jsonData.sensor}/>
    } else {
      lensContent = <SensorsEmptyText hasWriteAccess={hasWriteAccess} wizardButton={wizardButton}/>
      detectionCapabilityContent = <SensorsEmptyText hasWriteAccess={hasWriteAccess} wizardButton={wizardButton}/>
    }
    const detectionCapabilityTab = {'name': "Camera Performance", 'content': detectionCapabilityContent}
    tabs.push(detectionCapabilityTab)
    
    const sensorDescriptionTab = {
      "name": "Camera Description",
      "content": sensorDescriptionContent
    }
    tabs.push(sensorDescriptionTab)

    const type = jsonData.sensor?.lensType ?? ""
    const _title = supplier + " - " + component + " " + type + " V" + version
    setPageTitle(_title)

    const lensTab = {'name': 'Camera Lens', 'content': lensContent}

    tabs.push(lensTab)
    let adasUsageContent;
    adasUsageContent = <AdasUsage adasUsage={jsonData?.sensor?.adas_usage}/>
    const sensorAdasUsageTab = {
      "name": "Adas Usage",
      "content": adasUsageContent
    }
    tabs.push(sensorAdasUsageTab)
    
    refTitle.current = _title
    setTabs(tabs)
  }

  const wizardButton = <>{hasWriteAccess && <Tooltip title="Upload New Definition Wizard" arrow>
    <IconButton onClick={handleClickOpen} size={'large'}>
      <NoteAddOutlined style={{color: 'black'}}/>
    </IconButton>
  </Tooltip>}</>

  const downloadSourceButton = <>{isDownloadingFile ? <CircularProgress/> : hasWriteAccess && !isNewSensor && <Tooltip title="Download Source File" arrow>
    <IconButton onClick={handleDownloadSourceFile} size={'large'}>
      <FileDownloadIcon style={{color: 'black'}}/>
    </IconButton>
  </Tooltip>}</>

  const actionButtons = <ArgosFlexbox>
    {wizardButton}
    {downloadSourceButton}
  </ArgosFlexbox>

  const saveHandle = usePostRequest(addCameraConfigEndpoint)
  const onSaveConfiguration = async (configurationIndex: number | null, baselineData: any, configName: string, paramGmid: string) => {
    /**
     * Set the owner. Right now people can only override their own configs, but that could change
     */
    const isOverride = configurationIndex != null
    if (!isOverride) {
      baselineData["owner"] = currentUserGmid
    } else {
      baselineData["owner"] = paramGmid
    }

    // TODO: TrackedItemUid is just configId, and it is not actually, tracked, only the sensor is (the whole page, not configurations). So change this to configId, and possibly tracked configIds.
    let trackedItemUid = ''
    if (isOverride) {
      const search = window.location.search
      const o: any = queryString.parse(search)
      trackedItemUid = o[INNERVTAB_URL_PARAM]
    }

    // Deal with object properties
    const objectType = baselineData.objectCategory

    if (objectType !== 'Baseline') {
      const objectName = baselineData.objectName
      const objectVersion = baselineData.objectVersion
      if (objectType === 'Potentially Moving Object' || objectType === 'Miscellaneous') {
        const objectVariant = MAP_OBJECT_PROFILE_NAME_TO_KEY[baselineData.objectVariant]
        const objectVariantVal = refGroupedObjects.current[objectType][objectName][objectVersion][objectVariant]
        const objectId = objectVariantVal.id
        baselineData.objectId = objectId
      }

      // Remove unneeded fields
      delete baselineData.objectCategory
      delete baselineData.objectName
      delete baselineData.objectVersion
      delete baselineData.objectVariant
      delete baselineData.rcs

      delete baselineData.orientationWidth
      delete baselineData.orientationHeight
    }

    delete baselineData.resolution
    delete baselineData.verticalPixels
    delete baselineData.horizontalPixels
    delete baselineData.aspectRatio
    delete baselineData.resolutionMP

    delete baselineData.imagerComponent
    delete baselineData.imagerSupplier
    delete baselineData.imagerType
    delete baselineData.imagerVersion

    delete baselineData.dimensionValue

    saveHandle({
      "configName": configName,
      "configData": baselineData,
      "configIndex": configurationIndex,
      "currentUserGmid": currentUserGmid,
      isOverride,
      "id": id,
      "trackedItemUid": trackedItemUid,
    }).then((jsonData: any) => {
      processData(jsonData)
    }).catch((err) => {
      console.error(err)
      toast.error("Failed to add configuration")
    })
  }

  const deleteHandle = usePostRequest(deleteCameraConfigEndpoint)
  const onDeleteConfiguration = async (configurationName: string, gmid: string) => {
    const search = window.location.search
    let o: any = queryString.parse(search)
    const trackedItemUid = o[INNERVTAB_URL_PARAM]

    deleteHandle({
      "sensorId": id,
      "configId": "",  // TODO: Pass configId to delete the config
      "configName": configurationName,
      "owner": gmid,
      trackedItemUid
    })
      .then((jsonData: any) => {
        processData(jsonData)
      }).catch((err) => {
      console.log(err)
      toast.error("Failed to delete config.")
    });
  }
  const icons = (
    <div style={{display: 'flex', alignItems: 'center'}}>
      <CloseGlobalDialogButton/>
    </div>
  )
  const uploadFileUrl = `${config.url.API_URL}${uploadFileCameraEndpoint}`


  /**
   * Recently viewed
   */
  const recentlyViewedParams: ISetRecentlyViewedParams = {
    trackedItemUid: id,
    partitionKey: id,
    trackedItemType: ETrackedItemTypes.CAMERA_CONFIGURATION,
  }
  useSetRecentlyViewedWithDeps(recentlyViewedParams)
  useEffect(() => {
    if (pageTitle)
      document.title = sensorCameraHeaderTitle + ' - ' + pageTitle;
    return () => {
      document.title = sensorCameraHeaderTitle + ' - Landing';
    };
  }, [pageTitle]);
  return (
    // vertical flexbox with a header and horizontal tabs
    <Container>
      <div style={{height: '100%', display: 'flex', flexDirection: 'column', overflow: 'auto'}}>
        {/*header*/}
        <div>
          <div>
            <Toolbar variant="dense" style={{justifyContent: "space-between", background: '#3481d5', height: "100%"}}>
              <Typography sx={{color: '#ffffff', fontSize: 14}}>{pageTitle}</Typography>
              {icons}
            </Toolbar>
          </div>
        </div>
        {/*horizontal tabs*/}
        <Loader isLoading={isLoading}>
          {/*this is flex:1 so it takes up remaining vertical space that the header doesn't take up*/}
          <div style={{flex: 1, overflow: 'auto'}}>
            <HorizontalTabs initContent={null} initHtabIndex={0} horizontalTabs={tabs} align={'left'} uid={'htabs-1'} actionButtons={actionButtons}/>
          </div>
        </Loader>
      </div>
      <WizardDialog headerTitle={'Import Camera Wizard'}>
        <StepUploadFile uploadFileUrl={uploadFileUrl} messagesTitle={fileProcessingMessagesTitle}/>
        <InstructionsStep indexForErrorMessages={0} indexForWarningMessages={0} type={'general_info'} messagesTitle={errorMessagesTitle}/>
        <InstructionsStep indexForErrorMessages={1} indexForWarningMessages={1} type={'performance'} messagesTitle={errorMessagesTitle}/>
        <LensStep messagesTitle={errorMessagesTitle}/>
        <ImagerStep messagesTitle={errorMessagesTitle} id={sensorData?.id}/>
      </WizardDialog>
    </Container>

  )
}

export default WithWizardProvider({slice: cameraSlice})(ItemDetails)
